import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Grid,
  Typography,
  Card,
  Divider,
  InputLabel,
  Alert,
  Snackbar,
  useMediaQuery,
  Paper,
  CircularProgress,
} from "@mui/material";
import Sidenav from "../../common/Sidenav";
import Header from "../../common/Header";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { DropzoneArea } from "mui-file-dropzone";
import { config } from "../../../config/config";
import { apiList, invokeApi } from "../../../services/apiServices";
import { useCookies } from "react-cookie";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import IconSidenav from "../../common/IconSidenav";
import JoditEditor from "jodit-react";

const EditProgramme = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams(); // Get programme ID from URL params
  const programmeDataFromState = location.state?.programmeData;

  const [programmeDetails, setProgrammeDetails] = useState({
    programmeTitle: "",
    ogUrl: "",
    programmeContent: "",
    featuredUrl: ""
  });
  const [cookies] = useCookies();
  const [featuredImage, setFeaturedImage] = useState(null);
  const [selectedFeaturedImage, setSelectedFeaturedImage] = useState("");
  const [featuredImageChanged, setFeaturedImageChanged] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("");
  const [showSideNav, setShowSideNav] = useState(false);
  const isMobileScreen = useMediaQuery(
    "(min-width:360px) and (max-width:500px)"
  );
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(!programmeDataFromState);
  
  const toggleSideNav = () => {
    setShowSideNav(!showSideNav);
  };

  // Fetch programme data if not passed via state
  useEffect(() => {
    const fetchProgrammeData = async () => {
      if (!programmeDataFromState && id) {
        try {
          setFetching(true);
          const params = { id: id };
          const response = await invokeApi(
            config.getMyCollege + apiList.getProgramme,
            params,
            cookies
          );

          if (response?.status === 200) {
            const programmeData = response.data.programme;
            setProgrammeDetails({
              programmeTitle: programmeData.programmeTitle || "",
              ogUrl: programmeData.ogUrl || "",
              programmeContent: programmeData.programmeContent || "",
              featuredUrl: programmeData.featuredUrl || ""
            });
            setSelectedFeaturedImage(programmeData.featuredUrl || "");
          } else {
            showSnackbar("Failed to fetch programme data", "error");
            navigate("/programme-manager/programme-list");
          }
        } catch (error) {
          console.error("Error fetching programme data:", error);
          showSnackbar("Error fetching programme data", "error");
          navigate("/programme-manager/programme-list");
        } finally {
          setFetching(false);
        }
      } else if (programmeDataFromState) {
        // Use data from state if available
        setProgrammeDetails({
          programmeTitle: programmeDataFromState.programmeTitle || "",
          ogUrl: programmeDataFromState.ogUrl || "",
          programmeContent: programmeDataFromState.programmeContent || "",
          featuredUrl: programmeDataFromState.featuredUrl || ""
        });
        setSelectedFeaturedImage(programmeDataFromState.featuredUrl || "");
      }
    };

    fetchProgrammeData();
  }, [id, programmeDataFromState, cookies, navigate]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setProgrammeDetails((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleInputContentChange = (name, value) => {
    setProgrammeDetails((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleFeaturedImage = (files) => {
    if (files && files.length > 0) {
      setFeaturedImageChanged(true);
      const selectedFeaturedFile = files[0];
      const reader = new FileReader();
      reader.onload = () => {
        setSelectedFeaturedImage(reader.result);
        setFeaturedImage(selectedFeaturedFile);
      };
      reader.readAsDataURL(selectedFeaturedFile);
    } else {
      setSelectedFeaturedImage("");
      setFeaturedImage(null);
      setFeaturedImageChanged(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleAddFeaturedImage = async () => {
    if (!featuredImage) return programmeDetails.featuredUrl;

    const formData = new FormData();
    formData.append("featured", featuredImage);
    try {
      const response = await fetch(
        "https://api.reef.org.in/addProgrammeFeaturedImage",
        {
          method: "POST",
          body: formData,
        }
      );

      if (response?.status >= 200 && response?.status < 300) {
        const responseData = await response.json();
        return responseData.imageUrl || "";
      } else {
        showSnackbar("Failed to add programme featured image", "error");
        return programmeDetails.featuredUrl;
      }
    } catch (error) {
      showSnackbar("Something went wrong. Please try again later.", "error");
      return programmeDetails.featuredUrl;
    }
  };

  const handleUpdateProgramme = async (updatedProgrammeData) => {
    try {
      setLoading(true);
      const response = await invokeApi(
        config.getMyCollege + apiList.updateProgramme,
        updatedProgrammeData,
        cookies
      );

      if (response?.status >= 200 && response?.status < 300) {
        if (response.data.responseCode === "200") {
          showSnackbar("Programme updated successfully", "success");
          setTimeout(() => {
            navigate("/programme-manager/programme-list");
          }, 1000);
        } else if (response.data.responseCode === "400") {
          showSnackbar(response.data.message, "error");
        }
      }
    } catch (error) {
      console.error("Error during updating programme:", error);
      showSnackbar("Something went wrong. Please try again later!!", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleProgrammeSubmit = async (event) => {
    event.preventDefault();
    
    // Validation
    if (!programmeDetails.programmeTitle?.trim()) {
      showSnackbar("Programme title is required", "error");
      return;
    }

    if (!programmeDetails.ogUrl?.trim()) {
      showSnackbar("OG URL is required", "error");
      return;
    }

    try {
      const featuredUrl = featuredImageChanged
        ? await handleAddFeaturedImage()
        : programmeDetails.featuredUrl;

      const updatedProgrammeData = {
        id: id,
        programmeTitle: programmeDetails.programmeTitle,
        ogUrl: programmeDetails.ogUrl,
        programmeContent: programmeDetails.programmeContent,
        featuredUrl,
      };

      await handleUpdateProgramme(updatedProgrammeData);
    } catch (error) {
      console.error("Error while updating programme details:", error);
      showSnackbar("Something went wrong. Please try again later.", "error");
    }
  };

  if (fetching) {
    return (
      <Grid sx={{ display: "flex" }}>
        {isMobileScreen ? <IconSidenav /> : showSideNav ? <IconSidenav /> : <Sidenav />}
        <Grid component="main" sx={{ width: "100%", px: 1 }}>
          <Header toggleSideNav={toggleSideNav} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
            }}
          >
            <CircularProgress />
          </Box>
        </Grid>
      </Grid>
    );
  }

  return (
    <div>
      <Grid sx={{ display: "flex" }}>
        {isMobileScreen ? (
          <IconSidenav />
        ) : showSideNav ? (
          <IconSidenav />
        ) : (
          <Sidenav />
        )}
        <Grid component="main" sx={{ width: "100%", px: 1 }}>
          <Header toggleSideNav={toggleSideNav} />

          <Grid sx={{ width: "100%", px: isMobileScreen ? 1 : 5 }}>
            <Card elevation={1} sx={{ my: 5 }}>
              <Grid sx={{ py: isMobileScreen ? 2 : 2 }}>
                <Typography
                  sx={{
                    mb: 2,
                    mx: isMobileScreen ? 2 : 8,
                    textAlign: "left",
                    fontSize: isMobileScreen ? 14 : 18,
                    fontWeight: 600,
                    textTransform: "uppercase",
                  }}
                >
                  Edit Programme
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid
                  container
                  md={12}
                  xs={12}
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-around",
                    my: 2,
                  }}
                >
                  <Grid item md={11} xs={11} sx={{ my: 2 }}>
                    <InputLabel
                      sx={{
                        fontWeight: "bold",
                        my: 1,
                        fontSize: isMobileScreen ? 12 : 15,
                      }}
                    >
                      Programme Title *
                    </InputLabel>
                    <TextField
                      placeholder="Enter Programme Title"
                      fullWidth
                      name="programmeTitle"
                      value={programmeDetails.programmeTitle}
                      onChange={handleInputChange}
                      inputProps={{
                        style: {
                          height: isMobileScreen ? "3px" : "8px",
                          fontSize: isMobileScreen ? 12 : 14,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item md={11} xs={11} sx={{ my: 2 }}>
                    <InputLabel
                      sx={{
                        fontWeight: "bold",
                        my: 1,
                        fontSize: isMobileScreen ? 12 : 15,
                      }}
                    >
                      OG URL *
                    </InputLabel>
                    <TextField
                      placeholder="Enter OG URL"
                      fullWidth
                      name="ogUrl"
                      value={programmeDetails.ogUrl}
                      onChange={handleInputChange}
                      inputProps={{
                        style: {
                          height: isMobileScreen ? "3px" : "8px",
                          fontSize: isMobileScreen ? 12 : 14,
                        },
                      }}
                    />
                  </Grid>
                  
                  <Grid item md={11} xs={11} sx={{ my: 2 }}>
                    <InputLabel
                      sx={{
                        fontWeight: "bold",
                        my: 1,
                        fontSize: isMobileScreen ? 12 : 15,
                      }}
                    >
                      Programme Content
                    </InputLabel>
                    <Grid
                      item
                      xs={12}
                      sx={{
                        my: isMobileScreen ? 2 : 2,
                        mb: isMobileScreen ? 15 : 1,
                      }}
                    >
                      <JoditEditor
                        value={programmeDetails.programmeContent}
                        onChange={(newValue) =>
                          handleInputContentChange("programmeContent", newValue)
                        }
                        tabIndex={1}
                      />
                    </Grid>
                  </Grid>
                  
                  <Grid
                    container
                    md={12}
                    sx={{
                      width: "100%",
                      justifyContent: "center",
                      my: 5,
                    }}
                  >
                    <Grid item md={5} xs={11}>
                      <InputLabel
                        sx={{
                          fontWeight: "bold",
                          my: 1,
                          fontSize: isMobileScreen ? 12 : 15,
                        }}
                      >
                        Featured Image
                      </InputLabel>
                      <Grid
                        sx={{
                          my: 2,
                          ".MuiBox-root ": {
                            bgcolor: "#f0f8ff",
                            borderColor: "#1d1a57",
                            minHeight: 0,
                            my: 1,
                            alignItems: "center",
                            justifyContent: "center",
                          },
                        }}
                      >
                        <DropzoneArea
                          filesLimit={1}
                          acceptedFiles={["image/jpeg", "image/png"]}
                          onChange={handleFeaturedImage}
                          maxFileSize={5000000}
                          showPreviews={true}
                          showAlerts={false}
                          showPreviewsInDropzone={false}
                          showFileNames={false}
                          Icon={(props) => (
                            <CloudUploadIcon
                              style={{
                                color: "#1d1a57",
                                width: 35,
                                height: 35,
                              }}
                              {...props}
                            />
                          )}
                          dropzoneText={
                            <Typography
                              sx={{
                                color: "#1d1a57",
                                fontWeight: 600,
                                fontSize: 16,
                              }}
                            >
                              Upload / Drag files
                            </Typography>
                          }
                          previewText="Selected Featured Image:"
                        />
                      </Grid>
                      <Grid
                        sx={{
                          my: 3,
                          py: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "100%",
                          height: "auto",
                        }}
                      >
                        {selectedFeaturedImage && (
                          <img
                            src={selectedFeaturedImage}
                            alt="FeaturedUrl"
                            style={{ maxWidth: "90px" }}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <Divider sx={{ mb: 1 }} />
                <Grid
                  container
                  md={12}
                  xs={12}
                  sx={{
                    width: "100%",
                  }}
                >
                  <Divider sx={{ mb: 1 }} />
                  <Grid
                    container
                    spacing={isMobileScreen ? 0 : 2}
                    md={12}
                    xs={12}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mt: 2,
                    }}
                  >
                    <Button
                      color="success"
                      sx={{
                        mr: 4,
                        my: 2,
                        px: 2,
                        fontSize: 12,
                        background: "#1d1a57",
                        color: "#FFF",
                        fontWeight: 600,
                        "&:hover": {
                          background: "#1d1a57",
                          color: "#FFF",
                        },
                      }}
                      onClick={handleProgrammeSubmit}
                      disabled={loading}
                    >
                      {loading ? "Updating..." : "Save Changes"}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Card>
            <Snackbar
              open={openSnackbar}
              autoHideDuration={4000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              sx={{ width: "auto" }}
            >
              <Alert
                onClose={handleCloseSnackbar}
                severity={snackbarSeverity}
                sx={{ width: "auto" }}
              >
                {snackbarMessage}
              </Alert>
            </Snackbar>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default EditProgramme;